import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

/**
 * App-wide image lightbox. A single overlay (rendered by the provider) shows the
 * clicked image with a close button + prev/next navigation across every
 * lightboxable image currently on the page. Images opt in with a `data-lb-src`
 * attribute and an onClick that calls `useOpenLightbox()`; the opener gathers
 * the siblings from the DOM at click time (one thread/page is mounted at once),
 * so there's no per-thread wiring.
 */
type OpenFn = (items: string[], index: number) => void;
const LightboxCtx = createContext<OpenFn | null>(null);

const navBtn: CSSProperties = {
  position: 'fixed',
  width: 44,
  height: 44,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(255,255,255,0.15)',
  color: '#fff',
  fontSize: 26,
  lineHeight: 1,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10001,
};

export function LightboxProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<{ items: string[]; index: number } | null>(null);

  const open = useCallback<OpenFn>((items, index) => {
    if (items.length === 0) return;
    setState({ items, index: Math.min(Math.max(index, 0), items.length - 1) });
  }, []);
  const close = useCallback(() => setState(null), []);
  const move = useCallback((delta: number) => {
    setState((s) => (s ? { ...s, index: (s.index + delta + s.items.length) % s.items.length } : s));
  }, []);

  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') move(-1);
      else if (e.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close, move]);

  return (
    <LightboxCtx.Provider value={open}>
      {children}
      {state && (
        <div
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10000,
            background: 'rgba(0,0,0,0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
          }}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            style={{ ...navBtn, top: 16, right: 16, fontSize: 22 }}
          >
            ✕
          </button>
          {state.items.length > 1 && (
            <button
              type="button"
              aria-label="Previous"
              onClick={(e) => {
                e.stopPropagation();
                move(-1);
              }}
              style={{ ...navBtn, left: 16, top: '50%', transform: 'translateY(-50%)' }}
            >
              ‹
            </button>
          )}
          <img
            src={state.items[state.index]}
            alt=""
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '92vw', maxHeight: '92vh', objectFit: 'contain' }}
          />
          {state.items.length > 1 && (
            <button
              type="button"
              aria-label="Next"
              onClick={(e) => {
                e.stopPropagation();
                move(1);
              }}
              style={{ ...navBtn, right: 16, top: '50%', transform: 'translateY(-50%)' }}
            >
              ›
            </button>
          )}
          {state.items.length > 1 && (
            <div
              style={{
                position: 'fixed',
                bottom: 18,
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#fff',
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              {state.index + 1} / {state.items.length}
            </div>
          )}
        </div>
      )}
    </LightboxCtx.Provider>
  );
}

/**
 * Returns a click handler `(url) => void` that opens the lightbox at `url`,
 * navigating every `[data-lb-src]` image currently in the DOM. No-op outside a
 * LightboxProvider (e.g. the template-builder preview).
 */
export function useOpenLightbox() {
  const open = useContext(LightboxCtx);
  return useCallback(
    (url: string) => {
      if (!open) return;
      const items = Array.from(document.querySelectorAll<HTMLElement>('[data-lb-src]'))
        .map((el) => el.getAttribute('data-lb-src'))
        .filter((s): s is string => !!s);
      open(items.length ? items : [url], Math.max(0, items.indexOf(url)));
    },
    [open],
  );
}
